import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_DATE_PRESETS,
  EMPTY_ACTIVITY_FILTERS,
  activeActivityFilterKeys,
  clearActivityFilter,
  countActiveActivityFilters,
  type ActivityCategory,
  type ActivityDatePreset,
  type ActivityFilterChipKey,
  type ActivityFilterState,
} from '@/lib/patientActivity';

const selectClass = cn(
  TOOLBAR_CONTROL,
  'w-full font-normal text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring',
);

const fieldClass = cn(
  TOOLBAR_CONTROL,
  'w-full font-normal text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring',
);

export interface ActivityFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  filters: ActivityFilterState;
  onFiltersChange: (next: ActivityFilterState) => void;
}

export function ActivityFilters({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
}: ActivityFiltersProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeCount = countActiveActivityFilters(filters);
  const activeKeys = activeActivityFilterKeys(filters);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const chipLabel = (key: ActivityFilterChipKey): string => {
    switch (key) {
      case 'category':
        return t(`patients.activity.filter.category_${filters.category}`);
      case 'date':
        if (filters.datePreset === 'custom' && (filters.customFrom || filters.customTo)) {
          return [filters.customFrom, filters.customTo].filter(Boolean).join(' – ');
        }
        return t(`patients.activity.filter.date_${filters.datePreset}`);
      default: {
        const _exhaustive: never = key;
        return _exhaustive;
      }
    }
  };

  return (
    <div className="space-y-2">
      <div ref={rootRef} className="relative flex items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('patients.activity.search_placeholder', 'Search activity…')}
          className={cn(TOOLBAR_CONTROL, 'min-w-0 flex-1 font-normal')}
        />
        <Button
          type="button"
          variant="secondary"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'shrink-0',
            activeCount > 0 && 'border-ds-teal bg-ds-teal-tint text-ds-teal',
          )}
        >
          <Filter aria-hidden />
          {activeCount > 0
            ? t('patients.activity.filters_button_count', { count: activeCount })
            : t('patients.activity.filters_button', 'Filters')}
        </Button>

        {open && (
          <div
            role="dialog"
            aria-label={t('patients.activity.filters_title', 'Filters')}
            className="absolute right-0 top-full z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-ds-line-strong bg-ds-card p-3 shadow-pop"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className={UI_SECTION}>{t('patients.activity.filters_title', 'Filters')}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ds-muted">
                  {t('patients.activity.filter.category', 'Activity type')}
                </span>
                <select
                  value={filters.category}
                  onChange={(event) =>
                    onFiltersChange({
                      ...filters,
                      category: event.target.value as ActivityCategory,
                    })
                  }
                  className={selectClass}
                >
                  {ACTIVITY_CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`patients.activity.filter.category_${value}`)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ds-muted">
                  {t('patients.activity.filter.date', 'Date range')}
                </span>
                <select
                  value={filters.datePreset}
                  onChange={(event) =>
                    onFiltersChange({
                      ...filters,
                      datePreset: event.target.value as ActivityDatePreset,
                    })
                  }
                  className={selectClass}
                >
                  {ACTIVITY_DATE_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {t(`patients.activity.filter.date_${preset}`)}
                    </option>
                  ))}
                </select>
              </label>

              {filters.datePreset === 'custom' && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-ds-muted">
                      {t('patients.activity.filter.from', 'From')}
                    </span>
                    <input
                      type="date"
                      value={filters.customFrom}
                      onChange={(event) =>
                        onFiltersChange({ ...filters, customFrom: event.target.value })
                      }
                      className={fieldClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-ds-muted">
                      {t('patients.activity.filter.to', 'To')}
                    </span>
                    <input
                      type="date"
                      value={filters.customTo}
                      onChange={(event) =>
                        onFiltersChange({ ...filters, customTo: event.target.value })
                      }
                      className={fieldClass}
                    />
                  </label>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => onFiltersChange(EMPTY_ACTIVITY_FILTERS)}
              disabled={activeCount === 0}
              className="mt-3 text-sm font-medium text-ds-muted hover:text-ds-text disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t('patients.activity.filters_clear', 'Clear filters')}
            </button>
          </div>
        )}
      </div>

      {activeKeys.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeKeys.map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 rounded-full border border-ds-teal-line bg-ds-teal-tint px-2 py-0.5 text-xs font-medium text-ds-teal"
            >
              {chipLabel(key)}
              <button
                type="button"
                onClick={() => onFiltersChange(clearActivityFilter(filters, key))}
                aria-label={t('patients.activity.filters_remove_chip', { label: chipLabel(key) })}
                className="rounded-full hover:bg-ds-teal-line"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
